<template>
    <nomad-chat
        v-if="isWebComponentReady"
        :key="chatKey"
        ref="chatRef"
        :target-group="targetGroup || ''"
        :is-open="isNewChatOpen ? 'open' : ''"
        :class="[isNewChatOpen && 'new-chat__open']"
    >
    </nomad-chat>
</template>

<script>
import {mapActions, mapMutations} from "vuex";
import Cookies from "js-cookie";
import { getEnvironmentByOrigin } from '@/utils/getEnvironment'
import {OLD_TOKEN, WEB_MODULES_TOKEN} from "@/consts/newChat";

function patchNomadChatElement() {
    if (typeof window === 'undefined' || !window.customElements) return;
    const NomadChatElement = window.customElements.get('nomad-chat');
    if (NomadChatElement && !NomadChatElement.prototype.isDisconnectedPatched) {
        NomadChatElement.prototype.isDisconnectedPatched = true;
        const originalDisconnected = NomadChatElement.prototype.disconnectedCallback;
        NomadChatElement.prototype.disconnectedCallback = function() {
            if (this._app) {
                this._app.unmount();
                this._app = null;
            }
            if (originalDisconnected) {
                originalDisconnected.call(this);
            }
        };
    }
}

patchNomadChatElement();

export default {
    data() {
        return {
            chatKey: 0,
            isWebComponentReady: false,
            elementsClassesToHideList: [
                'header-lk',
                'grecaptcha-badge',
                'ws-chat',
                'tab-content',
                'fixed-mob-button',
            ],
            elementsClassesToFixPositionList: [
                'ws-chat',
            ],
        };
    },
    computed: {
        isNewChatOpen() {
            return this.$store.state.newChat.isNewChatOpen;
        },
        locale() {
            return this.$i18n.locale;
        },
        user() {
            return this.$store.state.auth.user;
        },
        token() {
            return this.$store.state.auth.webModulesToken;
        },
        preSelectedChatId() {
            return this.$store.state.newChat.preSelectedChatId;
        },
        targetGroup() {
            return this.$store.state.newChat.targetGroup;
        }
    },
    watch: {
        isNewChatOpen(newVal) {
            if (newVal) {
                if (this.$refs.chatRef && this.$refs.chatRef._app) {
                    this.$refs.chatRef._app.unmount();
                    this.$refs.chatRef._app = null;
                }
                this.chatKey++;
                this.$nextTick(() => {
                    this.openChatModal();
                    this.handleAutoMessage();
                });
            } else {
                this.closeChatModal();
            }
        },
        '$store.state.newChat.autoMessage': {
            handler(newVal) {
                if (newVal && this.isNewChatOpen) {
                    this.handleAutoMessage();
                }
            }
        },
        locale: {
            immediate: true,
            handler (newVal) {
                this.$refs.chatRef?.setAttribute("locale", newVal.toLowerCase());
            }
        },
        token: {
            handler(newVal, oldVal) {
                if (newVal && newVal !== oldVal) {
                    const currentToken = this.$refs.chatRef?.getAttribute('token');
                    if (currentToken !== newVal) {
                        this.updateToken(newVal);
                    }
                }
            }
        },
        preSelectedChatId: {
            immediate: true,
            handler (newVal) {
                if (Number(newVal)) {
                    this.$refs.chatRef?.setAttribute("initial-chat", newVal);
                    return;
                }
                this.$refs.chatRef?.setAttribute("initial-chat", '');
            }
        }
    },
    methods: {
        ...mapActions("newChat", ["closeNewChatModal", "clearAutoMessage", "clearTargetGroup"]),
        ...mapMutations("newChat", ["SET_UNREAD_MESSAGES_COUNT"]),
        ...mapMutations("auth", ["SET_WEB_MODULES_TOKEN"]),
        attachListeners() {
            if (!this.$refs.chatRef) return;
            this.removeListeners();
            this.$refs.chatRef.addEventListener("chat-close", this.closeChatModal);
            this.$refs.chatRef.addEventListener("update-unread", this.updateUnreadCounter);
            this.$refs.chatRef.addEventListener("update-token", this.onRefreshToken);
        },
        removeListeners() {
            if (!this.$refs.chatRef) return;
            this.$refs.chatRef.removeEventListener("chat-close", this.closeChatModal);
            this.$refs.chatRef.removeEventListener("update-unread", this.updateUnreadCounter);
            this.$refs.chatRef.removeEventListener("update-token", this.onRefreshToken);
        },
        openChatModal() {
            this.setEnvironment();
            this.setToken();
            this.updateLocale();
            if (Number(this.preSelectedChatId)) {
                this.$refs.chatRef?.setAttribute("initial-chat", this.preSelectedChatId);
            }
            this.$refs.chatRef?.setAttribute("target-group", this.targetGroup ?? '');
            this.$refs.chatRef?.setAttribute("is-open", 'open');
            this.attachListeners();
            this.hideNonChatElements();
        },
        handleAutoMessage() {
            const autoMessage = this.$store.state.newChat.autoMessage;
            if (autoMessage) {
                this.clearAutoMessage();
                this.$nextTick(() => {
                    if (this.$refs.chatRef) {
                        this.$refs.chatRef.autoMessage = autoMessage;
                    }
                });
            }
        },
        setEnvironment() {
            this.$refs.chatRef?.setAttribute("environment", getEnvironmentByOrigin());
        },
        closeChatModal() {
            this.$refs.chatRef?.setAttribute("is-open", '');
            this.$refs.chatRef?.setAttribute("target-group", '');
            this.closeNewChatModal();
            this.clearTargetGroup();
            this.showNonChatElements();
        },
        hideNonChatElements() {
            document.documentElement.classList.add('hide-elements-under-new-chat');
        },
        showNonChatElements() {
            document.documentElement.classList.remove('hide-elements-under-new-chat');
        },
        updateLocale() {
            this.$refs.chatRef?.setAttribute("locale", this.$i18n.locale);
        },
        setToken() {
            const token = Cookies.get(WEB_MODULES_TOKEN) ?? Cookies.get(OLD_TOKEN);
            const currentToken = this.$refs.chatRef?.getAttribute('token');
            if (token && currentToken !== token) {
                this.$refs.chatRef?.setAttribute("token", token);
                this.SET_WEB_MODULES_TOKEN(token);
            }
        },
        updateUnreadCounter(event) {
            this.SET_UNREAD_MESSAGES_COUNT(Number(event.detail) ?? 0);
        },
        updateToken(event) {
            const currentToken = this.$refs.chatRef?.getAttribute('token');
            if (event && currentToken !== event) {
                this.$refs.chatRef?.setAttribute("token", event);
            }
        },
        onRefreshToken(event) {
            if (!event?.detail) return;
            Cookies.set(WEB_MODULES_TOKEN, event.detail, {expires: 365});
            this.SET_WEB_MODULES_TOKEN(event.detail);
        },
    },
    mounted() {
        patchNomadChatElement();
        this.isWebComponentReady = true;
        this.$nextTick(() => {
            this.setEnvironment();
            this.setToken();
            this.updateLocale();
            this.attachListeners();
        });
    },
    beforeDestroy() {
        this.removeListeners();
        this.showNonChatElements();
        if (this.$refs.chatRef && this.$refs.chatRef._app) {
            this.$refs.chatRef._app.unmount();
            this.$refs.chatRef._app = null;
        }
    }
};
</script>
